import React, { useEffect, useRef, useState } from 'react';
import { paypalService, invitationService } from '../services/api';
import { Loader } from 'lucide-react';
import toast from 'react-hot-toast';

interface PayPalInvitationButtonProps {
  token: string;
  amount: number;
  onSuccess: (details: any) => void;
  onError?: (error: any) => void;
  onCancel?: () => void;
}

declare global {
  interface Window {
    paypal?: any;
  }
}

const PayPalInvitationButton: React.FC<PayPalInvitationButtonProps> = ({
  token,
  amount,
  onSuccess,
  onError,
  onCancel,
}) => {
  const paypalRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sdkReady, setSdkReady] = useState(false);

  // Charger le SDK PayPal
  useEffect(() => {
    const loadPayPalSDK = async () => {
      try {
        // Récupérer le client ID depuis l'API
        const response = await paypalService.getConfig();
        const clientId = response.data.client_id;

        if (!clientId) {
          setError('PayPal non configuré');
          setLoading(false);
          return;
        }

        // Vérifier si le SDK est déjà chargé
        if (window.paypal) {
          setSdkReady(true);
          setLoading(false);
          return;
        }

        // Charger le SDK
        const script = document.createElement('script');
        script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=EUR&intent=capture`;
        script.async = true;
        
        script.onload = () => {
          setSdkReady(true);
          setLoading(false);
        };
        
        script.onerror = () => {
          setError('Erreur de chargement PayPal');
          setLoading(false);
        };

        document.body.appendChild(script);
      } catch (err: any) {
        console.error('Erreur chargement PayPal:', err);
        setError(err.response?.data?.error || 'Erreur de configuration PayPal');
        setLoading(false);
      }
    };

    loadPayPalSDK();
  }, []);

  // Rendre les boutons PayPal une fois le SDK prêt
  useEffect(() => {
    if (!sdkReady || !paypalRef.current || !window.paypal) return;

    // Vider le conteneur
    paypalRef.current.innerHTML = '';

    window.paypal.Buttons({
      style: {
        layout: 'vertical',
        color: 'blue',
        shape: 'rect',
        label: 'paypal',
        height: 45,
      },

      // Créer la commande
      createOrder: async () => {
        try {
          const response = await invitationService.createPaypalOrder(token);
          if (response.data.success) {
            return response.data.order_id;
          } else {
            throw new Error(response.data.error || 'Erreur création commande');
          }
        } catch (err: any) {
          console.error('Erreur createOrder:', err);
          toast.error(err.response?.data?.error || 'Erreur lors de la création de la commande');
          throw err;
        }
      },

      // Capturer le paiement après approbation
      onApprove: async (data: any) => {
        try {
          const response = await invitationService.capturePaypal(token, data.orderID);
          if (response.data.success) {
            toast.success('Paiement PayPal réussi !');
            onSuccess(response.data);
          } else {
            throw new Error(response.data.error || 'Erreur capture paiement');
          }
        } catch (err: any) {
          console.error('Erreur capture:', err);
          toast.error(err.response?.data?.error || 'Erreur lors de la capture du paiement');
          if (onError) onError(err);
        }
      },

      // Annulation
      onCancel: () => {
        toast('Paiement annulé', { icon: '⚠️' });
        if (onCancel) onCancel();
      },

      // Erreur
      onError: (err: any) => {
        console.error('Erreur PayPal:', err);
        toast.error('Une erreur est survenue avec PayPal');
        if (onError) onError(err);
      },
    }).render(paypalRef.current);
  }, [sdkReady, token, onSuccess, onError, onCancel]);

  // Convertir FCFA en EUR pour affichage
  const amountEur = (amount / 655.957).toFixed(2);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader className="w-6 h-6 animate-spin text-blue-600 mr-2" />
        <span className="text-neutral-600">Chargement de PayPal...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700 text-sm">{error}</p>
        <p className="text-red-600 text-xs mt-1">
          Veuillez contacter l'administrateur ou choisir un autre mode de paiement.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-blue-800 text-sm">
          <strong>Paiement PayPal :</strong> Vous serez redirigé vers PayPal pour finaliser le paiement sécurisé.
        </p>
        <p className="text-blue-600 text-xs mt-1">
          Montant : {new Intl.NumberFormat('fr-FR').format(amount)} FCFA ≈ {amountEur} EUR
        </p>
      </div>
      
      <div ref={paypalRef} className="min-h-[50px]" />
    </div>
  );
};

export default PayPalInvitationButton;
